import { Injectable, DestroyRef, inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { AuthService } from '@modules/auth/services/auth.service';

export interface SseEvent {
  type: string;
  data: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class SseNotificationService {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  private readonly _events$ = new Subject<SseEvent>();
  private eventSource: EventSource | null = null;
  private initialized = false;

  readonly events$ = this._events$.asObservable();

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.authService.isAuthenticated$.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.connect();
      } else {
        this.disconnect();
      }
    });
  }

  on(eventType: string): Observable<SseEvent> {
    return this._events$.pipe(
      filter(event => event.type === eventType),
    );
  }

  private connect(): void {
    this.disconnect();

    const url = `${environment.apiUrl}events`;

    this.ngZone.runOutsideAngular(() => {
      this.eventSource = new EventSource(url, { withCredentials: true });

      const eventTypes = [
        'inquiry.new',
        'inquiry.new_message',
        'inquiry.closed',
        'presence.typing_start',
        'presence.typing_stop',
        'presence.unread_count_changed',
        'notification.new',
        'notification.unread_count_changed',
        'heartbeat',
      ];

      for (const type of eventTypes) {
        this.eventSource.addEventListener(type, (event: MessageEvent) => {
          this.ngZone.run(() => {
            try {
              const data = event.data ? JSON.parse(event.data) : {};
              this._events$.next({ type, data });
            } catch {
              this._events$.next({ type, data: {} });
            }
          });
        });
      }

      this.eventSource.onerror = () => {
        console.warn('[SSE] Connection error, will auto-reconnect');
      };
    });
  }

  private disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
